import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Format duration in milliseconds to human readable
const formatDuration = (ms) => {
  if (!ms || ms < 0) return '0s';
  
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

// Stage status icons and colors
const getStageStyle = (status) => {
  switch (status) {
    case 'COMPLETED':
      return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' };
    case 'FAILED':
      return { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
    case 'IN_PROGRESS':
      return { icon: Loader2, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', animate: true };
    default:
      return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-200' };
  }
};

// Live timer component that ticks every second
const LiveTimer = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (!startedAt) return;
    
    const startTime = new Date(startedAt).getTime();
    
    const updateElapsed = () => {
      setElapsed(Date.now() - startTime);
    };
    
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    
    return () => clearInterval(interval);
  }, [startedAt]);
  
  return (
    <span className="font-mono text-lg font-bold text-blue-600">
      {formatDuration(elapsed)}
    </span>
  );
};

// Single stage display component
const StageItem = ({ stage, showLive = false }) => {
  const style = getStageStyle(stage.status);
  const Icon = style.icon;
  
  const stageName = {
    'UPLOAD': 'Upload',
    'EXTRACTION': 'Extraction',
    'GENERATION': 'Generation'
  }[stage.stage] || stage.stage;
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${style.bg} ${style.border}`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${style.color} ${style.animate ? 'animate-spin' : ''}`} />
        <div>
          <span className="font-medium text-gray-800">{stageName}</span>
          {stage.status === 'IN_PROGRESS' && (
            <span className="ml-2 text-xs text-blue-600">In Progress...</span>
          )}
        </div>
      </div>
      <div className="text-right">
        {stage.status === 'IN_PROGRESS' && showLive ? (
          <LiveTimer startedAt={stage.started_at} />
        ) : stage.duration_ms ? (
          <span className="font-mono font-semibold text-gray-700">
            {formatDuration(stage.duration_ms)}
          </span>
        ) : (
          <span className="text-gray-400">--</span>
        )}
      </div>
    </div>
  );
};

// Main workflow timer component
export const WorkflowTimer = ({ caseId, onUpdate }) => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalDuration, setTotalDuration] = useState(0);
  
  const fetchStages = async () => {
    if (!caseId) return;
    
    try {
      const response = await fetch(`${API}/api/workflow-timer/case/${caseId}`);
      if (response.ok) {
        const data = await response.json();
        setStages(data.stages || []);
        setTotalDuration(data.total_duration_ms || 0);
        if (onUpdate) onUpdate(data);
      }
    } catch (error) {
      console.error('Error fetching workflow stages:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchStages();
    
    // Poll every 2 seconds if there's an in-progress stage
    const interval = setInterval(() => {
      const hasInProgress = stages.some(s => s.status === 'IN_PROGRESS');
      if (hasInProgress) {
        fetchStages();
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [caseId]);
  
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading timing data...</span>
      </div>
    );
  }
  
  if (stages.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-600 flex items-center gap-1">
          <Clock className="w-4 h-4" />
          Workflow Progress
        </span>
        {totalDuration > 0 && (
          <span className="text-sm text-gray-500">
            Total: <span className="font-semibold">{formatDuration(totalDuration)}</span>
          </span>
        )}
      </div>
      <div className="space-y-2">
        {stages.map((stage, idx) => (
          <StageItem key={stage.id || idx} stage={stage} showLive={true} />
        ))}
      </div>
    </div>
  );
};

// Compact inline timer for use in cards/lists
export const InlineTimer = ({ caseId, stage }) => {
  const [stageData, setStageData] = useState(null);
  
  useEffect(() => {
    if (!caseId) return;
    
    const fetchCurrentStage = async () => {
      try {
        const response = await fetch(`${API}/api/workflow-timer/case/${caseId}/current`);
        if (response.ok) {
          const data = await response.json();
          setStageData(data.current_stage);
        }
      } catch (error) {
        console.error('Error fetching current stage:', error);
      }
    };
    
    fetchCurrentStage();
    const interval = setInterval(fetchCurrentStage, 2000);
    
    return () => clearInterval(interval);
  }, [caseId]);
  
  if (!stageData || stageData.status !== 'IN_PROGRESS') return null;
  
  return (
    <div className="flex items-center gap-2 text-sm text-blue-600">
      <Loader2 className="w-3 h-3 animate-spin" />
      <span>{stageData.stage}</span>
      <LiveTimer startedAt={stageData.started_at} />
    </div>
  );
};

// Performance stats component for admin dashboard
export const PerformanceStats = ({ days = 30 }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/api/workflow-timer/performance?days=${days}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching performance stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [days]);
  
  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    );
  }
  
  if (!stats || !stats.stages) {
    return (
      <div className="text-center py-8">
        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">No performance data available yet</p>
        <p className="text-gray-400 text-xs mt-1">Data will appear after workflow operations are performed</p>
      </div>
    );
  }
  
  const stageOrder = ['UPLOAD', 'EXTRACTION', 'GENERATION'];
  const stageNames = { UPLOAD: 'Upload', EXTRACTION: 'Extraction', GENERATION: 'Generation' };
  const stageIcons = { UPLOAD: '📤', EXTRACTION: '🔍', GENERATION: '📄' };
  
  // Fixed color classes for Tailwind (dynamic classes don't work)
  const stageStyles = {
    UPLOAD: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: 'bg-blue-100'
    },
    EXTRACTION: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      icon: 'bg-green-100'
    },
    GENERATION: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700',
      icon: 'bg-purple-100'
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">Last {stats.period_days} days</div>
        <div className="text-xs text-gray-400">
          {Object.values(stats.stages).reduce((sum, s) => sum + (s.total_count || 0), 0)} total operations
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {stageOrder.map(stage => {
          const data = stats.stages[stage];
          const styles = stageStyles[stage];
          
          // Show placeholder card even if no data for this stage
          if (!data) {
            return (
              <div key={stage} className={`p-4 rounded-lg ${styles.bg} border ${styles.border} opacity-50`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">{stageIcons[stage]}</span>
                  <span className={`text-sm font-medium ${styles.text}`}>{stageNames[stage]}</span>
                </div>
                <div className="text-center py-2 text-gray-400 text-sm">No data yet</div>
              </div>
            );
          }
          
          return (
            <div key={stage} className={`p-4 rounded-lg ${styles.bg} border ${styles.border}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{stageIcons[stage]}</span>
                <span className={`text-sm font-medium ${styles.text}`}>{stageNames[stage]}</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Avg Time</span>
                  <span className="font-bold text-lg text-gray-900">{data.avg_duration_formatted || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Total Count</span>
                  <span className="font-semibold text-gray-800">{data.total_count || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 text-sm">Failure Rate</span>
                  <span className={`font-semibold ${(data.failure_rate || 0) > 5 ? 'text-red-600' : 'text-green-600'}`}>
                    {data.failure_rate || 0}%
                  </span>
                </div>
                {data.min_duration_ms && data.max_duration_ms && (
                  <div className="pt-2 mt-2 border-t border-gray-200">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Min: {formatDuration(data.min_duration_ms)}</span>
                      <span>Max: {formatDuration(data.max_duration_ms)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// Workflow Report Table Component - Shows detailed timing for each case
export const WorkflowReport = ({ days = 30, limit = 50, tenantId = null }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        let url = `${API}/api/workflow-timer/report?days=${days}&limit=${limit}`;
        if (tenantId) {
          url += `&tenant_id=${tenantId}`;
        }
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setReport(data);
        } else {
          setError('Failed to fetch report');
        }
      } catch (err) {
        console.error('Error fetching workflow report:', err);
        setError('Error loading report');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReport();
  }, [days, limit, tenantId]);
  
  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-10 bg-gray-200 rounded"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>{error}</p>
      </div>
    );
  }
  
  if (!report || !report.data || report.data.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">No workflow data available yet</p>
        <p className="text-gray-400 text-xs mt-1">Data will appear after documents are processed</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">Last {report.period_days} days</div>
        <div className="text-xs text-gray-400">{report.total_records} records</div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Opinion ID
              </th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span className="flex items-center justify-center gap-1">
                  📤 Upload
                </span>
              </th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span className="flex items-center justify-center gap-1">
                  🔍 Extraction
                </span>
              </th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span className="flex items-center justify-center gap-1">
                  📄 Generation
                </span>
              </th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                <span className="flex items-center justify-center gap-1">
                  ⏱️ Total
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {report.data.map((row, index) => (
              <tr key={row.case_id || index} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={row.opinion_id}>
                    {row.opinion_id?.substring(0, 12)}...
                  </div>
                  {row.started_at && (
                    <div className="text-xs text-gray-400">
                      {new Date(row.started_at).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    row.upload_ms ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {row.upload_formatted}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    row.extraction_ms ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {row.extraction_formatted}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    row.generation_ms ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {row.generation_formatted}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    row.total_ms > 0 ? 'bg-indigo-100 text-indigo-800 font-bold' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {row.total_formatted}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkflowTimer;
